import { fromLocalToUtc, formatInTz } from '@repletoryreach/sdk-utils/date';

/**
 * How the audience is chosen.
 *
 * `tag` is first, and the default, because tags are how this product groups
 * contacts: assembling a group means tagging people, and sending to it means
 * naming the tag. A `segment` is for a saved rule, and `paste` for the
 * genuinely ad-hoc handful.
 */
export type AudienceMode = 'tag' | 'segment' | 'paste';

/** Send it the moment it is created, or at a time the person picks. */
export type SendMode = 'now' | 'schedule';

/**
 * How it goes out (4 October 2026). WhatsApp is the default and its path is
 * unchanged; SMS and email are each offered only while the platform says that
 * channel is available.
 */
export type Channel = 'whatsapp' | 'sms' | 'email';

/**
 * What kind of SMS. `marketing` reaches only contacts who said yes to SMS
 * (WhatsApp consent never counts); `service` is a notice that is not a
 * promotion and reaches everyone who has not opted out of SMS.
 */
export type SmsKind = 'marketing' | 'service';

/**
 * What kind of email, on the same rule as SMS: `marketing` reaches only
 * contacts who agreed to email, `service` everyone not opted out of email. A
 * contact with no email address is never in an email audience either way.
 */
export type EmailKind = 'marketing' | 'service';

/** Whether a pasted line reads as an email address. The server decides; this only names lines it would refuse. */
export function looksLikeEmail(value: string): boolean {
  return /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/.test(value);
}

/** Whether a button link is one the server takes: absolute, http or https. */
export function isButtonLink(value: string): boolean {
  try {
    const u = new URL(value.trim());
    return (u.protocol === 'https:' || u.protocol === 'http:') && u.hostname.length > 0;
  } catch {
    return false;
  }
}

/**
 * Everything the create wizard holds.
 *
 * This is exactly what is saved as the server-side draft, so it must stay
 * small, plain and free of anything derived: the audience is NAMED (a tag, a
 * segment id) rather than listed, so a draft is a few hundred bytes whether it
 * addresses six people or six thousand.
 */
export interface BroadcastDraftState {
  step: number;
  /** WhatsApp unless SMS or email was chosen. Neither has a number or a template. */
  channel: Channel;
  phoneNumberId: string;
  audienceMode: AudienceMode;
  contactTag: string;
  segmentId: string;
  /** The paste box, verbatim. Truncated before saving; see MAX_PASTED_CHARS. */
  recipients: string;
  templateCode: string;
  /**
   * Which approved version of the template (Meta's language code, `en_US`, `af`): the one everybody gets, or,
   * with {@link BroadcastDraftState.perRecipientLanguage}, the one for people whose language has no version.
   * Empty in a draft saved before 1 November 2026: the first approved version of the code is taken then.
   */
  templateLanguage: string;
  /**
   * Send each person the version in their language (1 November 2026). `null` until the person touches the
   * switch, which reads as on when the template is approved in more than one language and off otherwise.
   */
  perRecipientLanguage: boolean | null;
  /** Placeholder position → the value to send, which may carry a contact token. */
  variables: Record<string, string>;
  headerMediaUrl: string;
  /** The SMS text, which may carry contact tokens. SMS only. */
  smsBody: string;
  smsKind: SmsKind;
  /** The email's subject line, which may carry contact tokens. Email only. */
  emailSubject: string;
  /** The email's text: paragraphs separated by a blank line, tokens and plain links. Email only. */
  emailBody: string;
  /** An optional button under the text; both or neither. Email only. */
  emailButtonLabel: string;
  emailButtonUrl: string;
  emailKind: EmailKind;
  throttle: number;
  sendMode: SendMode;
  /** `YYYY-MM-DDTHH:mm`, read in the ORGANISATION's timezone, never the browser's. */
  sendAt: string;
}

export const EMPTY_DRAFT: BroadcastDraftState = {
  step: 0,
  channel: 'whatsapp',
  phoneNumberId: '',
  audienceMode: 'tag',
  contactTag: '',
  segmentId: '',
  recipients: '',
  templateCode: '',
  templateLanguage: '',
  perRecipientLanguage: null,
  variables: {},
  headerMediaUrl: '',
  smsBody: '',
  smsKind: 'marketing',
  emailSubject: '',
  emailBody: '',
  emailButtonLabel: '',
  emailButtonUrl: '',
  emailKind: 'marketing',
  throttle: 60,
  sendMode: 'now',
  sendAt: ''
};

/**
 * The paste box is the one field that can grow without limit, and the service
 * refuses a draft over 64 KB. Cut it here, where the person can still see what
 * was cut, rather than losing the whole draft to a 400.
 */
export const MAX_PASTED_CHARS = 40_000;

/** Whether anything has been filled in: an untouched wizard is not worth guarding. */
export function hasContent(s: BroadcastDraftState): boolean {
  return Boolean(
    s.contactTag || s.segmentId || s.recipients.trim() || s.templateCode ||
    s.headerMediaUrl || s.sendAt || s.smsBody.trim() || s.emailSubject.trim() || s.emailBody.trim() ||
    s.emailButtonLabel.trim() || s.emailButtonUrl.trim() || Object.values(s.variables).some((v) => v.trim())
  );
}

/** Only the fields worth persisting, with the paste box cut to size. */
export function toSaved(s: BroadcastDraftState): Record<string, unknown> {
  return { ...s, recipients: s.recipients.slice(0, MAX_PASTED_CHARS) };
}

/**
 * A saved draft back into state, member by member.
 *
 * Read defensively rather than spread: the shape here changes with the wizard,
 * and a draft written by last week's version must not put a string where a
 * number belongs.
 */
export function fromSaved(raw: unknown): BroadcastDraftState {
  const o = (raw ?? {}) as Record<string, unknown>;
  const str = (k: string) => (typeof o[k] === 'string' ? (o[k] as string) : '');
  const vars = o.variables && typeof o.variables === 'object' && !Array.isArray(o.variables)
    ? Object.fromEntries(
      Object.entries(o.variables as Record<string, unknown>)
        .filter(([, v]) => typeof v === 'string') as [string, string][]
    )
    : {};
  const mode = str('audienceMode');
  const send = str('sendMode');
  return {
    step: typeof o.step === 'number' && o.step >= 0 && o.step <= 3 ? o.step : 0,
    channel: str('channel') === 'sms' ? 'sms' : str('channel') === 'email' ? 'email' : 'whatsapp',
    phoneNumberId: str('phoneNumberId'),
    audienceMode: mode === 'segment' || mode === 'paste' ? mode : 'tag',
    contactTag: str('contactTag'),
    segmentId: str('segmentId'),
    recipients: str('recipients'),
    templateCode: str('templateCode'),
    templateLanguage: str('templateLanguage'),
    perRecipientLanguage: typeof o.perRecipientLanguage === 'boolean' ? o.perRecipientLanguage : null,
    variables: vars,
    headerMediaUrl: str('headerMediaUrl'),
    smsBody: str('smsBody'),
    smsKind: str('smsKind') === 'service' ? 'service' : 'marketing',
    emailSubject: str('emailSubject'),
    emailBody: str('emailBody'),
    emailButtonLabel: str('emailButtonLabel'),
    emailButtonUrl: str('emailButtonUrl'),
    emailKind: str('emailKind') === 'service' ? 'service' : 'marketing',
    throttle: typeof o.throttle === 'number' && o.throttle >= 1 && o.throttle <= 200 ? o.throttle : 60,
    sendMode: send === 'schedule' ? 'schedule' : 'now',
    sendAt: str('sendAt')
  };
}

// ── Scheduling, in the organisation's timezone ─────────────────────────────

/**
 * The instant a `YYYY-MM-DDTHH:mm` means in the organisation's timezone.
 *
 * A `datetime-local` input has no timezone at all: the browser reads what is
 * typed in whatever zone the machine happens to be set to. Somebody in London
 * scheduling a Johannesburg shop's 09:00 message would have sent it at 11:00
 * local, and nothing on the old form said which zone it meant. The wizard names
 * the organisation's zone under the field and converts against it here, so the
 * time typed is the time the customer's own clock will show.
 */
export function scheduledInstant(local: string, timeZone: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local.trim());
  if (!m) return null;
  const [, y, mo, d, h, mi] = m.map(Number) as unknown as number[];
  const wall = new Date(y, mo - 1, d, h, mi, 0, 0);
  if (Number.isNaN(wall.getTime())) return null;
  return fromLocalToUtc(wall, timeZone);
}

/**
 * That instant written out in a zone, for "this goes out at …". The locale
 * comes from the app store, which `I18nProvider` keeps in step with the
 * console's language.
 */
export function describeInstant(at: Date, timeZone: string): string {
  return formatInTz(at, {
    timeZone,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/** `YYYY-MM-DDTHH:mm` for "now" in a zone, so the picker cannot offer the past. */
export function nowLocalValue(timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false
  }).formatToParts(new Date());
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00';
  const hour = get('hour') === '24' ? '00' : get('hour');
  return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`;
}

// ── Per-recipient personalisation ──────────────────────────────────────────

/**
 * A contact field a variable value may name, resolved per recipient by the
 * service as each message goes out.
 *
 * The list mirrors `Personalisation.KnownFields` in `services/broadcasts`. The
 * substitution itself is NOT done here: the preview endpoint returns each
 * sample contact's resolved variables, produced by the same code the runner
 * uses, so the preview cannot promise something the send does differently.
 */
export const CONTACT_FIELDS = ['name', 'email', 'locale', 'msisdn'] as const;
export type ContactBaseField = (typeof CONTACT_FIELDS)[number];

/**
 * A field a variable may be filled from: one of the four every contact has,
 * or `fields.<key>` for one of the organisation's own custom fields. The
 * service reads both through the same path (`{{contact.fields.city}}`), so a
 * custom field is addressed by its key exactly as it was defined, case and
 * all: `Fields` on the server is a case-sensitive map.
 */
export type ContactField = ContactBaseField | `fields.${string}`;

/**
 * The path grammar the service reads, mirrored: a base field name, or
 * `fields.` followed by a key of letters, digits, underscore, hyphen or space
 * (`Personalisation.TokenPattern` in `services/broadcasts`).
 */
const FIELD_PATH = /^(?:name|email|locale|msisdn|fields\.[A-Za-z0-9_\- ]+)$/;

/** Whether a path names something the service can read from a contact. */
export function isContactField(path: string): path is ContactField {
  if (!FIELD_PATH.test(path)) return false;
  return path.startsWith('fields.') || (CONTACT_FIELDS as readonly string[]).includes(path.toLowerCase());
}

/** The custom field's key, for a `fields.<key>` path; null for a base field. */
export function customFieldKey(field: ContactField): string | null {
  return field.startsWith('fields.') ? field.slice('fields.'.length) : null;
}

/** The token to drop into a variable value, with the fallback that keeps a send alive. */
export function contactToken(field: ContactField, fallback: string): string {
  const clean = fallback.trim();
  return clean ? `{{contact.${field}|${clean}}}` : `{{contact.${field}}}`;
}

/** Whether a value is personalised, so the wizard can say so rather than the person guessing. */
export function isPersonalised(value: string): boolean {
  return /\{\{\s*contact\./i.test(value);
}

// ── Media headers ──────────────────────────────────────────────────────────

/**
 * Deliberately a copy of the two helpers in the template editor rather than an
 * import from it. This page and that one are owned by different work and change
 * on different days; three lines of duplication is cheaper than a broken build
 * when either moves.
 */
export function isMediaFormat(f: string | undefined): boolean {
  return f === 'IMAGE' || f === 'VIDEO' || f === 'DOCUMENT';
}

/** What each header format will take, narrowed to the types WhatsApp itself accepts. */
const ACCEPT_FOR: Record<string, string[]> = {
  IMAGE: ['image/jpeg', 'image/png'],
  VIDEO: ['video/mp4', 'video/3gpp'],
  DOCUMENT: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  ]
};

export function acceptForHeader(format: string | undefined): string[] | undefined {
  return ACCEPT_FOR[(format ?? '').toUpperCase()];
}

/**
 * How one variable is being filled in: a literal, or a contact field with a
 * fallback.
 *
 * The wizard offers a field picker rather than a box you type `{{contact.name}}`
 * into, so nobody has to learn the token syntax to greet people by name, and a
 * typo cannot reach a customer as literal braces. The stored value is still the
 * token, because that is what the service reads.
 */
export interface VariableFill {
  field: ContactField | null;
  /** What goes out when the contact has nothing in that field. */
  fallback: string;
  /** The value when it is not personalised at all. */
  literal: string;
}

/**
 * A value that is nothing but one contact token, with an optional fallback.
 * The path group is the server's own (`Personalisation.TokenPattern`), so a
 * custom field's key round-trips with its spaces and hyphens intact. A value
 * with words around a token ("Hi {{contact.name}}") is a literal that the
 * service still resolves at send time; only a whole token flips the picker.
 */
const WHOLE_TOKEN = /^\{\{\s*contact\.([A-Za-z0-9_]+(?:\.[A-Za-z0-9_\- ]+)?)\s*(?:\|([^}]*?))?\s*\}\}$/i;

export function parseVariableValue(value: string): VariableFill {
  const m = WHOLE_TOKEN.exec((value ?? '').trim());
  if (m) {
    const path = m[1];
    const field: ContactField | null = path.toLowerCase().startsWith('fields.')
      ? `fields.${path.slice('fields.'.length)}`
      : (CONTACT_FIELDS as readonly string[]).includes(path.toLowerCase())
        ? path.toLowerCase() as ContactBaseField
        : null;
    if (field) return { field, fallback: m[2] ?? '', literal: '' };
  }
  return { field: null, fallback: '', literal: value ?? '' };
}

// ── Each person's language (1 November 2026) ───────────────────────────────

/** A template version as the template picker holds it: the code and Meta's language code, which name it together. */
export function versionKey(code: string, language: string): string {
  return `${code}|${language}`;
}

/** `[code, language]` back from {@link versionKey}. Template codes and language codes never hold a `|`. */
export function fromVersionKey(key: string): [string, string] {
  const at = key.indexOf('|');
  return at < 0 ? [key, ''] : [key.slice(0, at), key.slice(at + 1)];
}

/**
 * Whether each person is sent the version in their language: what the person chose, else on when the template
 * is approved in more than one language. Never on for a template with one version, where there is nothing to choose.
 */
export function sendsPerLanguage(s: BroadcastDraftState, versions: number): boolean {
  return versions > 1 && (s.perRecipientLanguage ?? true);
}
